import { Container, Divider, Grid } from "@material-ui/core";
import ApexCharts from "apexcharts";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { Header, HeaderWelcome, Inputs, Text } from "components";
import ExportPdfButton from "components/buttons/export-pdf-button";
import { useConfig } from "context/config.context";
import { businessProfessionals, getBusiness } from "services/businessService";
import { getMe } from "services/meService";
import { getDashboard } from "services/reports.service";
import businessAction from "store/actions/businessAction";
import { isMobile } from "utils/isMobile";
import { getCurrency, moneyToLocale } from "utils/utils";
import { getAppSecondColorDashboard } from "utils/validators";
import { generateReportPDF } from "utils/pdfGenerator";

import i18n from "../../../i18n";

import "../style.scss";

let timerNextFind = null;

const Reports = ({ inside = false }) => {
	const { t } = useTranslation();

	const { control, watch, setValue } = useForm({
		reValidateMode: "onChange",
		mode: "onChange",
	});

	const [loaded, setLoaded] = useState(false);
	const [currency, setCurrency] = useState("");
	const [currencySymbol, setCurrencySymbol] = useState("pt-BR");
	const [professionals, setProfessionals] = useState([]);
	const [exportingPdf, setExportingPdf] = useState(false);
	const [data, setData] = useState({
		sales_total: 0,
		manualBookings_total: 0,
		onlineBookings_total: 0,
		clients_total: 0,
	});

	const fromDateRef = useRef(null);
	const toDateRef = useRef(null);
	const professionalRef = useRef(null);

	const chartSalesRef = useRef(null);
	const chartNumbersRef = useRef(null);
	const chartClientsRef = useRef(null);

	const navigate = useNavigate();
	const dispatch = useDispatch();
	const business = useSelector((state) => state.business.business);
	const { handleLoading, handleFeedback, setTypeAccess } = useConfig();

	const { token, business_id, language } = useParams();

	const handleKeyDown = (e, nextFieldRef) => {
		if (e.key === "Enter") {
			e.preventDefault();
			nextFieldRef.current.focus();
		}
	};

	const onSubmit = async (data, _business = business) => {
		if (chartSalesRef.current) {
			chartSalesRef.current.destroy();
		}
		if (chartNumbersRef.current) {
			chartNumbersRef.current.destroy();
		}
		if (chartClientsRef.current) {
			chartClientsRef.current.destroy();
		}

		handleLoading(true);
		const values = {
			business_id: business_id ?? _business.id,
			from_date: treatDate(data.from_date),
			to_date: treatDate(data.to_date),
			professional_id: data.professional_id === "todos" ? null : data.professional_id,
		};

		try {
			const result = await getDashboard(values);

			// Ordenar o array result pelo campo competence
			result.sort((a, b) => new Date(a.competence) - new Date(b.competence));

			setData({
				sales_total: result.reduce((acc, curr) => acc + curr.income, 0) / 100,
				manualBookings_total: result.reduce((acc, curr) => acc + curr.manualBookings, 0),
				onlineBookings_total: result.reduce((acc, curr) => acc + curr.onlineBookings, 0),
				clients_total: result.reduce((acc, curr) => acc + curr.clients, 0),
			});
			handleLoading(false);
			updateChart({
				sales: result.map((item) => item.income / 100),
				manualBookings: result.map((item) => item.manualBookings),
				onlineBookings: result.map((item) => item.onlineBookings),
				clients: result.map((item) => item.clients),
				months: result.map((item) => formatDate(item.competence)),
			});
		} catch (err) {
			console.log("~ err:", err);
			setTypeAccess("reports");
			handleLoading(false);
			navigate("/login");
		}
	};

	const treatDate = (date) => {
		const dateObject = date.split("-");
		const day = dateObject[2];
		const month = dateObject[1];
		const year = dateObject[0];
		return `${year}-${month}-${day}`;
	};

	const formatDate = (date) => {
		const dateObject = date.split("-");
		const month = dateObject[1];
		let monthName = "";
		const year = dateObject[0];

		switch (Number(month)) {
			case 1:
				monthName = t("reports.months.january");
				break;
			case 2:
				monthName = t("reports.months.february");
				break;
			case 3:
				monthName = t("reports.months.march");
				break;
			case 4:
				monthName = t("reports.months.april");
				break;
			case 5:
				monthName = t("reports.months.may");
				break;
			case 6:
				monthName = t("reports.months.june");
				break;
			case 7:
				monthName = t("reports.months.july");
				break;
			case 8:
				monthName = t("reports.months.august");
				break;
			case 9:
				monthName = t("reports.months.september");
				break;
			case 10:
				monthName = t("reports.months.october");
				break;
			case 11:
				monthName = t("reports.months.november");
				break;
			case 12:
				monthName = t("reports.months.december");
				break;
		}

		return `${monthName.substring(0, 3)}/${year}`;
	};

	const chartOptions = {
		chart: {
			type: "bar",
			toolbar: {
				show: false,
			},
		},
		series: [
			{
				name: t("reports.series.sales"),
				data: data.sales,
			},
		],
		title: {
			text: t("reports.title.sales"),
			align: "center",
			offsetY: 25,
			style: {
				fontSize: "1.2rem",
				fontWeight: "bold",
				fontFamily: undefined,
				color: "#263238",
			},
		},
		fill: {
			opacity: 1,
		},
		colors: ["#008FFB", getAppSecondColorDashboard()],
		plotOptions: {
			bar: {
				dataLabels: {
					position: "top",
				},
			},
		},
		dataLabels: {
			enabled: true,
			offsetY: -20,
			style: {
				fontSize: "12px",
				colors: ["#304758"],
			},
		},
	};

	const updateChart = (_data) => {
		const sales_doc = document.getElementById("sales-chart");
		if (sales_doc) {
			const currencyGetSymbol = getCurrency(currency ? currency : business?.currency);
			chartSalesRef.current = new ApexCharts(sales_doc, {
				...chartOptions,
				xaxis: {
					categories: _data.months,
				},
				series: [
					{
						name: `${t("reports.series.sales")} (${currencyGetSymbol})`,
						data: _data.sales,
					},
				],
				title: {
					...chartOptions.title,
					text: `${t("reports.title.sales")} (${currencyGetSymbol})`,
				},
				yaxis: {
					labels: {
						formatter: function (val) {
							return val.toLocaleString(currencySymbol, {
								minimumFractionDigits: 0,
								maximumFractionDigits: 2,
							});
						},
					},
				},
				tooltip: {
					y: {
						formatter: function (val) {
							return (
								currency +
								val.toLocaleString("pt-BR", {
									minimumFractionDigits: 2,
									maximumFractionDigits: 2,
								})
							);
						},
					},
					style: {
						fontSize: "12px",
						color: "#111",
					},
				},
				dataLabels: {
					...chartOptions.dataLabels,
					enabled: false,
					style: {
						colors: ["#000"],
					},
				},
			});
			chartSalesRef.current.render();
		}
		const numbers_doc = document.getElementById("numbers-chart");
		if (numbers_doc) {
			chartNumbersRef.current = new ApexCharts(numbers_doc, {
				...chartOptions,
				xaxis: {
					categories: _data.months,
				},
				series: [
					{
						name: t("reports.series.onlineBookings"),
						data: _data.onlineBookings,
					},
					{
						name: t("reports.series.manualBookings"),
						data: _data.manualBookings,
					},
				],
				title: {
					...chartOptions.title,
					text: t("reports.title.bookings"),
				},
			});
			chartNumbersRef.current.render();
		}

		const clients_doc = document.getElementById("clients-chart");
		if (clients_doc) {
			chartClientsRef.current = new ApexCharts(clients_doc, {
				...chartOptions,
				xaxis: {
					categories: _data.months,
				},
				series: [
					{
						name: t("reports.series.clients"),
						data: _data.clients,
					},
				],
				title: {
					...chartOptions.title,
					text: t("reports.title.clients"),
				},
			});
			chartClientsRef.current.render();
		}
	};

	const updateFields = (_business = business) => {
		const list = [];

		if (_business?.professionals.length > 1) {
			list.push({
				label: t("reports.fields.all"),
				value: "todos",
			});
		}

		_business?.professionals.forEach((professional) => {
			list.push({
				label: professional.name,
				value: professional.id,
			});
		});

		setProfessionals(list);
		setValue("professional_id", list[0].value);

		onSubmit(
			{
				professional_id: list[0].value === "todos" ? null : list[0].value,
				from_date: new Date(new Date().setDate(new Date().getDate() - 60)).toISOString().split("T")[0],
				to_date: new Date().toISOString().split("T")[0],
			},
			_business
		);
	};

	const handleCompareDates = () => {
		const fromDate = new Date(watch("from_date"));
		const toDate = new Date(watch("to_date"));
		const diffTime = Math.abs(toDate - fromDate);
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		if (diffDays > 365 || diffDays < 0) {
			handleFeedback(t("reports.alerts.attention"), t("reports.messages.attentionPeriod"), "warning", 5000);
		} else {
			if (fromDate > toDate) {
				handleFeedback(t("reports.alerts.attention"), t("reports.messages.attentionDates"), "warning", 5000);
				setValue("from_date", watch("to_date"));
			}
		}
	};

	useEffect(() => {
		handleCompareDates();
	}, [watch("from_date"), watch("to_date")]);

	useEffect(() => {
		handleLoading(true);
		if (!business && !inside && !token) {
			needLoginMessage();
			return;
		}
		
		if (business !== null && !token) {
			updateLanguage();
			updateFields();
			handleLoading(false);
			return;
		}

		if (token && !loaded) {
			if (!business_id) {
				needLoginMessage();
				return;
			}
			validateProfessional();
		}

		return () => {
			handleLoading(false);
		};
	}, [business, token, business_id, language]);

	const updateLanguage = (lang = language) => {
		if (lang) {
			switch (lang.toLowerCase()) {
				case "en":
				case "english":
					i18n.changeLanguage("enUS");
					setCurrencySymbol("en-US");
					break;
				case "es":
				case "spanish":
					i18n.changeLanguage("esES");
					setCurrencySymbol("es-ES");
					break;
				default:
					i18n.changeLanguage("ptBR");
					setCurrencySymbol("pt-BR");
					break;
			}
		} else if (business?.language) {
			switch (business.language.toLowerCase()) {
				case "english":
					i18n.changeLanguage("enUS");
					setCurrencySymbol("en-US");
					break;
				case "spanish":
					i18n.changeLanguage("esES");
					setCurrencySymbol("es-ES");
					break;
				default:
					i18n.changeLanguage("ptBR");
					setCurrencySymbol("pt-BR");
					break;
			}
		}
	};

	const validateProfessional = async () => {
		handleLoading(true);
		try {
			localStorage.setItem("accessToken", token);
			const _me = await getMe();
			const _businessProfessionals = await businessProfessionals();

			let professional = null;
			let foundBusiness = null;

			_businessProfessionals.data.find((business) => {
				if (business.id === business_id) {
					foundBusiness = business;
					professional = business.professionals.find((professional) => professional.phone === _me.data.phone);
				}
			});

			if (professional && foundBusiness) {
				setLoaded(true);
				await dispatch(businessAction.updateBusiness(foundBusiness));
				
				const locale = moneyToLocale(foundBusiness.phone);
				const currency = getCurrency(foundBusiness.currency);
				
				setCurrency(currency);
				setCurrencySymbol(locale);

				updateLanguage();
				updateFields(foundBusiness);
				handleLoading(false);
			} else {
				console.error('Professional or business not found:', { professional, foundBusiness });
				validateBusiness();
			}
		} catch (err) {
			console.error("Error in validateProfessional:", err);
			validateBusiness();
		}
	};

	const validateBusiness = async () => {
		handleLoading(true);
		try {
			const res = await getBusiness();
			if (res.data.length > 0) {
				const business = res.data.find((business) => business.id === business_id);
				if (business) {
					setLoaded(true);
					await dispatch(businessAction.updateBusiness(business));
					updateLanguage();
					updateFields(business);
					handleLoading(false);
				} else {
					console.error('Business not found with id:', business_id);
					needLoginMessage();
				}
			} else {
				console.error('No businesses found in response');
				needLoginMessage();
			}
		} catch (err) {
			console.error("Error in validateBusiness:", err);
			needLoginMessage();
		}
	};

	const needLoginMessage = () => {
		handleLoading(false);
		handleFeedback(t("reports.alerts.attention"), t("reports.messages.attention"), "warning", 5000);
		setTypeAccess("reports");
		navigate("/login");
	};

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: "smooth" });
	}, []);

	const updateReport = () => {
		clearTimeout(timerNextFind);
		timerNextFind = setTimeout(() => {
			onSubmit({
				professional_id: watch("professional_id"),
				from_date: watch("from_date"),
				to_date: watch("to_date"),
			});
		}, 1500);
	};

	const handleExportPdf = async () => {
		setExportingPdf(true);
		try {
			const selectedProfessional = professionals.find(
				p => p.value === watch("professional_id")
			);
			const isProfessional = watch("professional_id") !== "todos";
			const professionalName = selectedProfessional?.label || t("reports.fields.all");

			const result = await generateReportPDF({
				business,
				fromDate: watch("from_date"),
				toDate: watch("to_date"),
				professionalName,
				isProfessional,
				data,
				currency,
				currencySymbol,
				translations: {
					detailedReport: t("reports.title.detailedReport"),
					businessReport: t("reports.title.businessReport"),
					professionalReport: t("reports.title.professionalReport"),
					period: t("reports.fields.period"),
					to: t("reports.fields.to"),
					professional: t("reports.fields.professional"),
					business: t("reports.fields.business"),
					all: t("reports.fields.all"),
					sales: t("reports.series.sales"),
					clients: t("reports.series.clients"),
					manualBookings: t("reports.series.manualBookings"),
					onlineBookings: t("reports.series.onlineBookings"),
					bookings: t("reports.series.bookings"),
					salesChart: t("reports.title.sales"),
					generatedAt: t("reports.fields.generatedAt"),
					averageTicket: t("reports.fields.averageTicket"),
					occupancyRate: t("reports.fields.occupancyRate"),
					dailyAverage: t("reports.fields.dailyAverage"),
					clientsPerDay: t("reports.fields.clientsPerDay"),
					totalBookings: t("reports.fields.totalBookings"),
					onlinePercentage: t("reports.fields.onlinePercentage"),
					manualPercentage: t("reports.fields.manualPercentage"),
					online: "Online",
					manual: "Manual",
					distribution: t("reports.fields.distribution"),
				},
			});

			if (result.success) {
				if (result.isWebView) {
					const message = result.message || t("reports.messages.pdfSentToApp") || "PDF sent to mobile app";
					handleFeedback(t("reports.alerts.success"), message, "success");
				} else {
					handleFeedback(t("reports.alerts.success"), t("reports.messages.pdfGenerated"), "success");
				}
			} else {
				throw new Error(result.error || "PDF generation failed");
			}
		} catch (error) {
			handleFeedback(t("reports.alerts.error"), t("reports.messages.pdfError"), "error");
		} finally {
			setExportingPdf(false);
		}
	};

	return (
		<>
			{inside && (
				<>
					<Header title={t("reports.title.reports")} showNotifications />
					<Divider />
				</>
			)}
			<Container
				maxWidth="sm"
				className="box-dashboard"
				style={{
					marginLeft: inside ? "0px" : "auto",
					width: isMobile() ? "100%" : "50%",
					maxWidth: inside ? "100%" : "600px",
				}}>
				{!inside && <HeaderWelcome showMessage={false} message={t("reports.title.reports")} />}
				<div className="content-dashboard">
					<div className="box-reports">
						<Grid container spacing={2}>
							<Grid item xs={12} md={6}>
								<Inputs
									control={control}
									name="from_date"
									type="date"
									label={t("reports.fields.from")}
									inputRef={fromDateRef}
									handleKeyDown={(e) => handleKeyDown(e, toDateRef)}
									value={new Date(new Date().setDate(new Date().getDate() - 60)).toISOString().split("T")[0]}
									getChange={() => updateReport()}
								/>
							</Grid>
							
							<Grid item xs={12} md={6}>
								<Inputs
									control={control}
									name="to_date"
									type="date"
									label={t("reports.fields.to")}
									inputRef={toDateRef}
									handleKeyDown={(e) => handleKeyDown(e, professionalRef)}
									value={new Date().toISOString().split("T")[0]}
									getChange={() => updateReport()}
								/>
							</Grid>
						</Grid>
					</div>
					
					<div className="box-reports">
						<Grid container spacing={2}>
							<Grid item xs={12} md={12}>
								<Inputs
									control={control}
									name="professional_id"
									type="select"
									inputRef={professionalRef}
									handleKeyDown={(e) => handleKeyDown(e, null)}
									value="0"
									className="select-person"
									style={{ width: "100%" }}
									options={professionals}
									getChange={() => updateReport()}
								/>
							</Grid>
						</Grid>
					</div>

					<div className="box-reports">
						<Grid item xs={12} md={6}>
							<ExportPdfButton onClick={handleExportPdf} loading={exportingPdf} />
						</Grid>
					</div>

					<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
						<div className="box-reports numbers">
							<Text variant="body" align="center" style={{ color: "#393762" }}>
								{t("reports.series.sales")}
							</Text>
							<Text variant="big" align="center" style={{ color: "#393762" }}>
								{currency}
								{data.sales_total.toLocaleString(currencySymbol, {
									minimumFractionDigits: 2,
									maximumFractionDigits: 2,
								})}
							</Text>
						</div>
						<div className="box-reports numbers">
							<Text variant="body" align="center" style={{ color: "#393762" }}>
								{t("reports.series.clients")}
							</Text>
							<Text variant="big" align="center" style={{ color: "#393762" }}>
								{data.clients_total}
							</Text>
						</div>
						<div className="box-reports numbers">
							<Text variant="body" align="center" style={{ color: "#393762" }}>
								{t("reports.series.manualBookings")}
							</Text>
							<Text variant="big" align="center" style={{ color: "#393762" }}>
								{data.manualBookings_total}
							</Text>
						</div>
						<div className="box-reports numbers">
							<Text variant="body" align="center" style={{ color: "#393762" }}>
								{t("reports.series.onlineBookings")}
							</Text>
							<Text variant="big" align="center" style={{ color: "#393762" }}>
								{data.onlineBookings_total}
							</Text>
						</div>
					</div>

					<div className="dashboard-content" id="sales-chart" />
					<div className="dashboard-content" id="numbers-chart" />
					<div className="dashboard-content" id="clients-chart" />
				</div>
			</Container>
		</>
	);
};

export default Reports;
